import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { asyncApiCall } from '../../api';
import { getEligibleVouchersApi } from '../apiOuigoRefunds';
import { mapFinalizedVouchersResponse } from '../mappers';
import { VOUCHERS_PAGE_SIZE, createEmptyPagination } from '../utils';

const loadEligibleVouchersRequest = (parameters) =>
	asyncApiCall(getEligibleVouchersApi, parameters);

const useAvailableVouchers = ({
	appliedFromDate,
	appliedToDate,
	currentPage,
	enabled,
	intl,
	loadEligibleVouchers = loadEligibleVouchersRequest,
	sortBy,
}) => {
	const [vouchers, setVouchers] = useState([]);
	const [pagination, setPagination] = useState(createEmptyPagination);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [filter, setFilter] = useState(null);

	const loadVouchers = useCallback(async () => {
		if (!enabled) {
			return null;
		}

		setIsLoading(true);
		setHasError(false);

		try {
			const response = await loadEligibleVouchers({
				startDate: appliedFromDate
					? format(appliedFromDate, 'yyyy-MM-dd')
					: undefined,
				endDate: appliedToDate
					? format(appliedToDate, 'yyyy-MM-dd')
					: undefined,
				page: currentPage + 1,
				pageSize: VOUCHERS_PAGE_SIZE,
				sortBy,
			});
			const mappedResponse = mapFinalizedVouchersResponse(response, intl);

			setVouchers(mappedResponse.vouchers);
			setPagination(mappedResponse.pagination);
			setFilter(response?.filter || null);
			return mappedResponse;
		} catch {
			setVouchers([]);
			setPagination(createEmptyPagination());
			setHasError(true);
			return null;
		} finally {
			setIsLoading(false);
		}
	}, [
		appliedFromDate,
		appliedToDate,
		currentPage,
		enabled,
		intl,
		loadEligibleVouchers,
		sortBy,
	]);

	useEffect(() => {
		loadVouchers();
	}, [loadVouchers]);

	const removeVouchers = (voucherIds) => {
		setVouchers((currentVouchers) =>
			currentVouchers.filter((voucher) => !voucherIds.includes(voucher.id)),
		);
		setPagination((currentPagination) => {
			const totalItems = Math.max(
				currentPagination.totalItems - voucherIds.length,
				0,
			);

			return {
				...currentPagination,
				currentPage: 0,
				totalItems,
				totalPages: Math.ceil(totalItems / currentPagination.pageSize),
				resultStart: totalItems > 0 ? 1 : 0,
				resultEnd: Math.min(currentPagination.pageSize, totalItems),
			};
		});
	};

	return {
		filter,
		hasError,
		isLoading,
		pagination,
		reload: loadVouchers,
		removeVouchers,
		vouchers,
	};
};

export default useAvailableVouchers;
